import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LineChart, Line, Legend, Tooltip } from 'recharts';
import { useChartHover, formatTick } from './chartHover';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://169.58.21.41/api';
const getHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

function detectKind(sensor) {
    const n = sensor?.name || '';
    if (/meteo/i.test(n)) return 'METEO';
    if (/exo/i.test(n))   return 'EXO';
    if (/vwc/i.test(n))   return 'VWC';
    if (/flow/i.test(n))  return 'FLOW';
    return 'GENERIC';
}

function stripPrefix(key) {
    return key.replace(/^[0-9a-f]+-/i, '');
}

function flattenReading(r) {
    const out = { t: r.recordedAt };
    if (r.extra && typeof r.extra === 'object') {
        for (const [k, v] of Object.entries(r.extra)) {
            if (typeof v === 'number') out[stripPrefix(k)] = v;
        }
    }
    if (r.temperature  != null && out.Temperature  == null) out.Temperature  = r.temperature;
    if (r.ph           != null && out.pH           == null) out.pH           = r.ph;
    if (r.dissolvedO2  != null && out.DO           == null) out.DO           = r.dissolvedO2;
    if (r.turbidity    != null && out.Turbitity    == null) out.Turbitity    = r.turbidity;
    if (r.ec           != null && r.ec > 0 && out.Conductivity == null) out.Conductivity = r.ec;
    if (r.humidity     != null && out.R_Humidity   == null) out.R_Humidity   = r.humidity;
    if (r.rainfall     != null && out.Precipitation == null) out.Precipitation = r.rainfall;
    if (r.windSpeed    != null && out['W.Speed']   == null) out['W.Speed']   = r.windSpeed;
    if (r.soilMoisture != null) out._soilMoisture = r.soilMoisture;
    if (r.soilTemp     != null) out._soilTemp     = r.soilTemp;
    return out;
}

function fmtX(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

// ── Chart card with HTML overlay tooltip ─────────────────────────────────────
function ChartWithTooltip({ data, dataKey, color, gradId, unit, title }) {
    const { wrapRef, tipRef, lineRef, chartProps } = useChartHover({
        length: data.length,
        renderTip: (idx) => {
            const point = data[idx];
            const value = point[dataKey];
            if (value == null || isNaN(value)) return null;
            return `
            <div class="chart-tip-time">${fmtX(point.t)}</div>
            <div class="chart-tip-val" style="color:${color}">
                ${Number(value).toFixed(dec)}<span class="chart-tip-unit">${unit || ''}</span>
            </div>`;
        },
    });

    const vals = data.map(d => d[dataKey]).filter(v => v != null && !isNaN(v));
    if (vals.length === 0) return null;

    const mn  = Math.min(...vals);
    const mx  = Math.max(...vals);
    const dec = mx < 10 ? 3 : mx < 100 ? 2 : 1;
    // Series that sit near zero (wind, rain) start at 0; others (temperature, pressure) get rounded auto bounds.
    const yMin = mn >= 0 && mn <= (mx - mn) / 2 ? 0 : 'auto';
    const yMax = mn === 0 && mx === 0 ? 1 : 'auto';

    return (
        <div ref={wrapRef} className="chart-card">
            <div className="chart-card-title">
                {title}{unit ? <span className="chart-card-unit"> ({unit})</span> : null}
            </div>
            <div className="chart-card-range">
                <span>{mn.toFixed(dec)}</span><span>{mx.toFixed(dec)}</span>
            </div>

            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top:2, right:4, left:0, bottom:0 }} {...chartProps}>
                    <defs>
                        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor={color} stopOpacity={0.20}/>
                            <stop offset="95%" stopColor={color} stopOpacity={0.01}/>
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="2 4" stroke="rgba(148,163,184,0.12)" vertical={false}/>
                    <XAxis dataKey="t" tickFormatter={fmtX} tick={{ fontSize:9, fill:'#94a3b8' }}
                        minTickGap={70} axisLine={false} tickLine={false}/>
                    <YAxis domain={[yMin, yMax]} width={44} tick={{ fontSize:9, fill:'#94a3b8' }}
                        axisLine={false} tickLine={false} tickCount={5}
                        tickFormatter={formatTick}/>
                    <Tooltip content={() => null} cursor={false} isAnimationActive={false}/>
                    <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.8}
                        fill={`url(#${gradId})`} dot={false} isAnimationActive={false}/>
                </AreaChart>
            </ResponsiveContainer>

            {/* Crosshair vertical line */}
            <div ref={lineRef} className="chart-crosshair" />

            {/* Tooltip bubble */}
            <div ref={tipRef} className="chart-tip" />
        </div>
    );
}

// ── Wind rose ────────────────────────────────────────────────────────────────
function WindRose({ pts }) {
    const sectors = Array.from({ length:16 }, (_, i) => ({
        deg: i*22.5, count:0, totalSpeed:0,
    }));
    let hasData = false;
    for (const d of pts) {
        const dir = d['W.Direction'], spd = d['W.Speed'];
        if (dir == null || spd == null || isNaN(dir) || isNaN(spd)) continue;
        hasData = true;
        const idx = Math.round((((Number(dir)%360)+360)%360)/22.5)%16;
        sectors[idx].count++;
        sectors[idx].totalSpeed += Number(spd);
    }
    if (!hasData) return null;
    const maxCount = Math.max(...sectors.map(s=>s.count), 1);
    const cx=110, cy=110, maxR=85;
    const sc = spd => spd<2?'#93c5fd':spd<4?'#3b82f6':spd<7?'#22c55e':spd<12?'#eab308':'#ef4444';
    return (
        <div style={{ background:'#fff', borderRadius:10, padding:'12px 14px',
            boxShadow:'0 1px 3px rgba(0,0,0,0.06)', border:'1px solid #eef0f3' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#374151', marginBottom:8,
                textTransform:'uppercase', letterSpacing:'0.04em' }}>Wind Rose</div>
            <div style={{ display:'flex', gap:14, alignItems:'center' }}>
                <svg width={220} height={220} style={{ flexShrink:0 }}>
                    {[0.25,0.5,0.75,1].map(f=><circle key={f} cx={cx} cy={cy} r={maxR*f} fill="none" stroke="#e5e7eb" strokeWidth={1}/>)}
                    {[0,45,90,135].map(a=>{const r=(a*Math.PI)/180; return <line key={a} x1={cx+maxR*Math.sin(r)} y1={cy-maxR*Math.cos(r)} x2={cx-maxR*Math.sin(r)} y2={cy+maxR*Math.cos(r)} stroke="#e5e7eb" strokeWidth={1}/>;}) }
                    {sectors.map((s,i)=>{
                        if(s.count===0) return null;
                        const r=(s.count/maxCount)*maxR, half=Math.PI/16, mid=((s.deg-90)*Math.PI)/180;
                        const x1=cx+r*Math.cos(mid-half), y1=cy+r*Math.sin(mid-half);
                        const x2=cx+r*Math.cos(mid+half), y2=cy+r*Math.sin(mid+half);
                        return <path key={i} d={`M${cx},${cy} L${x1},${y1} A${r},${r} 0 0 1 ${x2},${y2} Z`}
                            fill={sc(s.count?s.totalSpeed/s.count:0)} fillOpacity={0.85} stroke="#fff" strokeWidth={0.5}/>;
                    })}
                    {['N','NE','E','SE','S','SW','W','NW'].map((l,i)=>{
                        const a=((i*45-90)*Math.PI)/180;
                        return <text key={l} x={cx+(maxR+16)*Math.cos(a)} y={cy+(maxR+16)*Math.sin(a)}
                            textAnchor="middle" dominantBaseline="middle" fontSize={10} fontWeight={700} fill="#374151">{l}</text>;
                    })}
                </svg>
                <div style={{ fontSize:11, display:'flex', flexDirection:'column', gap:4 }}>
                    {[['#93c5fd','0–2 m/s'],['#3b82f6','2–4 m/s'],['#22c55e','4–7 m/s'],['#eab308','7–12 m/s'],['#ef4444','12+ m/s']].map(([c,l])=>(
                        <div key={l} style={{ display:'flex',alignItems:'center',gap:6 }}>
                            <div style={{ width:11,height:11,borderRadius:3,background:c }}/>
                            <span style={{ color:'#6b7280' }}>{l}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ── Multi-line chart (VWC) ────────────────────────────────────────────────────
function MultiLineChart({ pts, lines, title }) {
    const valid = pts.filter(d => lines.some(l => d[l.key] != null));

    const { wrapRef, tipRef, lineRef, chartProps } = useChartHover({
        length: valid.length,
        tipWidth: 165,
        renderTip: (idx) => {
            const point = valid[idx];
            const rows = lines.map(l => {
                const v = point[l.key];
                if (v == null) return '';
                return `<div class="chart-tip-row">
                <span class="chart-tip-label" style="color:${l.color}">${l.label}</span>
                <span class="chart-tip-row-val">${Number(v).toFixed(2)}</span>
            </div>`;
            }).join('');
            return `<div class="chart-tip-time">${fmtX(point.t)}</div>${rows}`;
        },
    });

    if (valid.length === 0) return null;

    return (
        <div ref={wrapRef} className="chart-card chart-card--wide">
            <div className="chart-card-title">{title}</div>
            <ResponsiveContainer width="100%" height={180}>
                <LineChart data={valid} margin={{ top:4, right:4, left:0, bottom:0 }} {...chartProps}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.12)" vertical={false}/>
                    <XAxis dataKey="t" tickFormatter={fmtX} tick={{ fontSize:9, fill:'#94a3b8' }}
                        minTickGap={60} axisLine={false} tickLine={false}/>
                    <YAxis width={44} tick={{ fontSize:9, fill:'#94a3b8' }} axisLine={false} tickLine={false} tickCount={4}/>
                    <Tooltip content={() => null} cursor={false} isAnimationActive={false}/>
                    <Legend wrapperStyle={{ fontSize:10, paddingTop:6 }}/>
                    {lines.map(l => (
                        <Line key={l.key} type="monotone" dataKey={l.key} name={l.label}
                            stroke={l.color} strokeWidth={2} dot={false}
                            isAnimationActive={false} connectNulls/>
                    ))}
                </LineChart>
            </ResponsiveContainer>

            <div ref={lineRef} className="chart-crosshair" />
            <div ref={tipRef} className="chart-tip" />
        </div>
    );
}

// ── Chart grid ─────────────────────────────────────────────────────────────────
function ChartGrid({ children }) {
    return <div className="chart-grid">{children}</div>;
}

// ── CSV export ────────────────────────────────────────────────────────────────
function exportCSV(rows, columns, sensorName) {
    const header = ['Date', 'Time', ...columns.map(c => c.label)].join(',');
    const body = rows.map(r => {
        const d = new Date(r.t);
        const date = `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
        const time = `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
        const vals = columns.map(c => r[c.key] != null ? Number(r[c.key]).toFixed(c.dec ?? 2) : '');
        return [date, time, ...vals].join(',');
    }).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `${sensorName}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
}

// ── Data table ────────────────────────────────────────────────────────────────
function DataTable({ pts, columns, sensorName }) {
    const [page,      setPage]      = useState(1);
    const [startDate, setStartDate] = useState('');
    const [endDate,   setEndDate]   = useState('');
    const PER_PAGE = 25;

    const filtered = pts.filter(r => {
        if (!startDate && !endDate) return true;
        const d = new Date(r.t);
        if (startDate && d < new Date(startDate)) return false;
        if (endDate   && d > new Date(endDate + 'T23:59:59')) return false;
        return true;
    });

    const total    = Math.ceil(filtered.length / PER_PAGE);
    const pageRows = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

    const thS = { padding:'8px 12px', textAlign:'left', fontWeight:700, fontSize:11,
        color:'#64748b', textTransform:'uppercase', letterSpacing:'0.04em',
        whiteSpace:'nowrap', background:'#f8fafc', borderBottom:'1px solid #e5e7eb' };
    const tdS = { padding:'7px 12px', color:'#374151', whiteSpace:'nowrap',
        borderBottom:'1px solid #f1f5f9', fontSize:12 };

    return (
        <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:12,
            overflow:'hidden', marginTop:20 }}>
            {/* Toolbar */}
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px',
                borderBottom:'1px solid #f1f5f9', flexWrap:'wrap' }}>
                <div style={{ fontWeight:700, fontSize:13, flex:1, color:'#111827' }}>
                    📋 {sensorName} — Geçmiş ({filtered.length} kayıt)
                </div>
                <input type="date" value={startDate}
                    onChange={e => { setStartDate(e.target.value); setPage(1); }}
                    style={{ border:'1px solid #e5e7eb', borderRadius:6,
                        padding:'4px 8px', fontSize:11, color:'#374151' }} />
                <span style={{ fontSize:11, color:'#94a3b8' }}>→</span>
                <input type="date" value={endDate}
                    onChange={e => { setEndDate(e.target.value); setPage(1); }}
                    style={{ border:'1px solid #e5e7eb', borderRadius:6,
                        padding:'4px 8px', fontSize:11, color:'#374151' }} />
                <button
                    onClick={() => exportCSV(filtered, columns, sensorName)}
                    style={{ background:'#6366f1', color:'#fff', border:'none',
                        borderRadius:8, padding:'6px 14px', cursor:'pointer',
                        fontSize:12, fontWeight:700, whiteSpace:'nowrap' }}>
                    ⬇ CSV
                </button>
            </div>

            {/* Table */}
            <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                        <tr>
                            <th style={thS}>Date</th>
                            <th style={thS}>Time</th>
                            {columns.map(c => <th key={c.key} style={thS}>{c.label}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.length === 0 ? (
                            <tr><td colSpan={2 + columns.length}
                                style={{ textAlign:'center', padding:24, color:'#94a3b8', fontSize:13 }}>
                                No data
                            </td></tr>
                        ) : pageRows.map((r, i) => {
                            const d = new Date(r.t);
                            const date = `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
                            const time = `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
                            return (
                                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                                    <td style={tdS}>{date}</td>
                                    <td style={tdS}>{time}</td>
                                    {columns.map(c => (
                                        <td key={c.key} style={{ ...tdS, fontWeight:600, color:'#0f172a' }}>
                                            {r[c.key] != null
                                                ? `${Number(r[c.key]).toFixed(c.dec ?? 2)}${c.unit ? ' ' + c.unit : ''}`
                                                : '—'}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {total > 1 && (
                <div style={{ display:'flex', justifyContent:'center', alignItems:'center',
                    gap:8, padding:'10px 16px', borderTop:'1px solid #f1f5f9' }}>
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                        style={{ background:'#f1f5f9', border:'none', borderRadius:6,
                            padding:'4px 12px', cursor:'pointer', fontSize:13, fontWeight:700,
                            opacity: page === 1 ? 0.4 : 1 }}>←</button>
                    <span style={{ fontSize:12, color:'#64748b' }}>{page} / {total}</span>
                    <button disabled={page === total} onClick={() => setPage(p => p + 1)}
                        style={{ background:'#f1f5f9', border:'none', borderRadius:6,
                            padding:'4px 12px', cursor:'pointer', fontSize:13, fontWeight:700,
                            opacity: page === total ? 0.4 : 1 }}>→</button>
                </div>
            )}
        </div>
    );
}

// ── Range picker ──────────────────────────────────────────────────────────────
function RangePicker({ range, setRange }) {
    return (
        <div style={{ display:'flex', gap:6, marginBottom:12 }}>
            {[{l:'24h',v:1},{l:'3d',v:3},{l:'7d',v:7},{l:'30d',v:30}].map(p=>(
                <button key={p.v} onClick={()=>setRange(p.v)} style={{
                    padding:'3px 11px', borderRadius:20, fontSize:11, fontWeight:700, cursor:'pointer',
                    border: range===p.v ? '2px solid #6366f1':'1px solid #e5e7eb',
                    background: range===p.v ? '#6366f1':'#fff',
                    color: range===p.v ? '#fff':'#6b7280',
                }}>{p.l}</button>
            ))}
        </div>
    );
}

const EXO_CHARTS = [
    { key:'Temperature',  label:'Temperature',  unit:'°C',    color:'#e879a6', grad:'exo_temp' },
    { key:'Conductivity', label:'EC',            unit:'μS/cm', color:'#f59e0b', grad:'exo_ec'   },
    { key:'DO',           label:'DO',            unit:'mg/L',  color:'#3b82f6', grad:'exo_do'   },
    { key:'pH',           label:'pH',            unit:'',      color:'#10b981', grad:'exo_ph'   },
    { key:'Turbitity',    label:'Turbidity',     unit:'NTU',   color:'#8b5cf6', grad:'exo_turb' },
    { key:'ORP',          label:'ORP',           unit:'mV',    color:'#ef4444', grad:'exo_orp'  },
    { key:'TDS',          label:'TDS',           unit:'mg/L',  color:'#0ea5e9', grad:'exo_tds'  },
    { key:'Salinity',     label:'Salinity',      unit:'ppt',   color:'#14b8a6', grad:'exo_sal'  },
];

const METEO_CHARTS = [
    { key:'A_Pressure',    label:'Atmospheric Pressure', unit:'hPa',  color:'#6366f1', grad:'m_pres' },
    { key:'A.Temperature', label:'Air Temperature',      unit:'°C',   color:'#ef4444', grad:'m_temp' },
    { key:'Precipitation', label:'Precipitation',        unit:'mm',   color:'#0ea5e9', grad:'m_rain' },
    { key:'R_Humidity',    label:'Relative Humidity',    unit:'%',    color:'#10b981', grad:'m_hum'  },
    { key:'S.Radiation',   label:'Solar Radiation',      unit:'W/m²', color:'#f59e0b', grad:'m_sol'  },
    { key:'V.Pressure',    label:'Vapour Pressure',      unit:'Pa',   color:'#8b5cf6', grad:'m_vp'   },
    { key:'W.Speed',       label:'Wind Speed',           unit:'m/s',  color:'#14b8a6', grad:'m_ws'   },
];

const VWC_LINES = [
    { key:'VWC_15cm',            label:'VWC 15cm',       color:'#0ea5e9' },
    { key:'VWC_50cm',            label:'VWC 50cm',       color:'#6366f1' },
    { key:'S.Temperature_15cm',  label:'Soil Temp 15cm', color:'#ef4444' },
    { key:'S.Temperature_50cm',  label:'Soil Temp 50cm', color:'#f97316' },
    { key:'S.Conductivity_15cm', label:'Soil EC 15cm',   color:'#10b981' },
    { key:'S.Conductivity_50cm', label:'Soil EC 50cm',   color:'#14b8a6' },
];

const EXO_COLS = [
    { key:'Temperature',  label:'Temp (°C)',   unit:'°C',    dec:2 },
    { key:'Conductivity', label:'EC (μS/cm)',  unit:'μS/cm', dec:1 },
    { key:'DO',           label:'DO (mg/L)',   unit:'mg/L',  dec:2 },
    { key:'pH',           label:'pH',          unit:'',      dec:2 },
    { key:'Turbitity',    label:'Turb (NTU)',  unit:'NTU',   dec:1 },
    { key:'ORP',          label:'ORP (mV)',    unit:'mV',    dec:1 },
    { key:'TDS',          label:'TDS (mg/L)',  unit:'mg/L',  dec:1 },
    { key:'Salinity',     label:'Sal (ppt)',   unit:'ppt',   dec:3 },
];

const METEO_COLS = [
    { key:'A.Temperature', label:'Air Temp (°C)',   unit:'°C',   dec:1 },
    { key:'R_Humidity',    label:'Humidity (%)',    unit:'%',    dec:1 },
    { key:'A_Pressure',    label:'Pressure (hPa)',  unit:'hPa',  dec:1 },
    { key:'Precipitation', label:'Rain (mm)',       unit:'mm',   dec:1 },
    { key:'W.Speed',       label:'Wind (m/s)',      unit:'m/s',  dec:1 },
    { key:'S.Radiation',   label:'Radiation (W/m²)',unit:'W/m²', dec:1 },
    { key:'V.Pressure',    label:'VapPres (Pa)',    unit:'Pa',   dec:1 },
];

const VWC_COLS = [
    { key:'VWC_15cm',            label:'VWC 15cm (%)',     unit:'%',    dec:2 },
    { key:'VWC_50cm',            label:'VWC 50cm (%)',     unit:'%',    dec:2 },
    { key:'S.Temperature_15cm',  label:'SoilTemp 15cm',   unit:'°C',   dec:2 },
    { key:'S.Temperature_50cm',  label:'SoilTemp 50cm',   unit:'°C',   dec:2 },
    { key:'S.Conductivity_15cm', label:'SoilEC 15cm',     unit:'dS/m', dec:3 },
    { key:'S.Conductivity_50cm', label:'SoilEC 50cm',     unit:'dS/m', dec:3 },
];

const FLOW_COLS = [
    { key:'Liter', label:'Consumption (L)', unit:'L', dec:2 },
];

// ── Main component ────────────────────────────────────────────────────────────
export default function InsighioSensorDetail({ sensor, language = 'en', refreshInterval = 0 }) {
    const [range,       setRange]       = useState(30);
    const [readings,    setReadings]    = useState([]);
    const [loading,     setLoading]     = useState(false);
    const [error,       setError]       = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);

    const kind = detectKind(sensor);

    const loadData = useCallback(() => {
        if (!sensor?.id) return;
        setLoading(true);
        setError(null);
        const to   = new Date();
        const from = new Date(to.getTime() - range * 86400000);
        const url  = `${API_URL}/sensors/${sensor.id}/history?limit=1000&from=${from.toISOString()}&to=${to.toISOString()}`;
        fetch(url, { headers: getHeaders() })
            .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
            .then(data => {
                setReadings(Array.isArray(data) ? [...data].reverse() : []);
                setLastRefresh(new Date());
            })
            .catch(e  => setError(String(e)))
            .finally(() => setLoading(false));
    }, [sensor?.id, range]);

    // Initial + range-change load
    useEffect(() => {
        setReadings([]);
        loadData();
    }, [loadData]);

    // Auto-refresh interval
    useEffect(() => {
        if (!refreshInterval || refreshInterval <= 0) return;
        const timerId = setInterval(loadData, refreshInterval * 60 * 1000);
        return () => clearInterval(timerId);
    }, [refreshInterval, loadData]);

    const pts = useMemo(() => readings.map(flattenReading), [readings]);

    if (!sensor) return null;

    return (
        <div style={{ padding:'2px 0' }}>
            {/* Header */}
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, flexWrap:'wrap' }}>
                <span style={{ fontWeight:800, fontSize:15, color:'#111827', letterSpacing:'-0.02em' }}>{sensor.name}</span>
                <span style={{ background:'#dbeafe', color:'#1d4ed8', fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20 }}>insigh.io</span>
                <span style={{ background:'#d1fae5', color:'#065f46', fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20 }}>● ACTIVE</span>
                {lastRefresh && (
                    <span style={{ marginLeft:'auto', fontSize:10, color:'#9ca3af' }}>
                        Last updated: {lastRefresh.toLocaleTimeString()}
                        {refreshInterval > 0 && (
                            <span style={{ color:'#6366f1', marginLeft:6 }}>↻ every {refreshInterval} min</span>
                        )}
                    </span>
                )}
            </div>

            <RangePicker range={range} setRange={setRange} />

            {loading && (
                <div style={{ textAlign:'center', padding:36, color:'#6b7280', fontSize:13 }}>
                    <div style={{ fontSize:20, marginBottom:6 }}>⏳</div>Sensör verisi yükleniyor…
                </div>
            )}
            {error && (
                <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8,
                    padding:'10px 14px', color:'#dc2626', fontSize:13, marginBottom:12 }}>
                    ⚠️ {error}
                </div>
            )}
            {!loading && !error && pts.length === 0 && (
                <div style={{ textAlign:'center', padding:36, color:'#9ca3af', fontSize:13 }}>
                    <div style={{ fontSize:24, marginBottom:6 }}>📭</div>Bu aralıkta veri yok
                </div>
            )}

            {!loading && pts.length > 0 && kind === 'EXO' && (<>
                <ChartGrid>
                    {EXO_CHARTS.map(c => (
                        <ChartWithTooltip key={c.key}
                            data={pts.filter(d => d[c.key] != null && !isNaN(d[c.key]))}
                            dataKey={c.key} title={c.label} unit={c.unit}
                            color={c.color} gradId={c.grad} />
                    ))}
                </ChartGrid>
                <DataTable pts={[...pts].reverse()} columns={EXO_COLS} sensorName={sensor.name} />
            </>)}

            {!loading && pts.length > 0 && kind === 'METEO' && (<>
                <ChartGrid>
                    {METEO_CHARTS.map(c => (
                        <ChartWithTooltip key={c.key}
                            data={pts.filter(d => d[c.key] != null && !isNaN(d[c.key]))}
                            dataKey={c.key} title={c.label} unit={c.unit}
                            color={c.color} gradId={c.grad} />
                    ))}
                    <WindRose pts={pts} />
                </ChartGrid>
                <DataTable pts={[...pts].reverse()} columns={METEO_COLS} sensorName={sensor.name} />
            </>)}

            {!loading && pts.length > 0 && kind === 'VWC' && (<>
                <ChartGrid>
                    <MultiLineChart
                        pts={pts}
                        lines={VWC_LINES.filter(l => pts.some(d => d[l.key] != null))}
                        title="Soil Sensor Readings" />
                </ChartGrid>
                <DataTable pts={[...pts].reverse()} columns={VWC_COLS.filter(c => pts.some(d => d[c.key] != null))} sensorName={sensor.name} />
            </>)}

            {!loading && pts.length > 0 && kind === 'FLOW' && (<>
                <ChartGrid>
                    <ChartWithTooltip
                        data={pts.filter(d => d.Liter != null && !isNaN(d.Liter))}
                        dataKey="Liter" title="Water Consumption" unit="L"
                        color="#0ea5e9" gradId="flow_l" />
                </ChartGrid>
                <DataTable pts={[...pts].reverse()} columns={FLOW_COLS} sensorName={sensor.name} />
            </>)}

            {!loading && pts.length > 0 && kind === 'GENERIC' && (() => {
                const genericCols = Object.keys(pts[0] || {})
                    .filter(k => k !== 't' && !k.startsWith('_') && pts.some(d => typeof d[k] === 'number'))
                    .map(k => ({ key: k, label: k, unit: '', dec: 2 }));
                return (<>
                    <ChartGrid>
                        {genericCols.map((c, i) => (
                            <ChartWithTooltip key={c.key}
                                data={pts.filter(d => d[c.key] != null && !isNaN(d[c.key]))}
                                dataKey={c.key} title={c.key} unit=""
                                color={['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6'][i % 6]}
                                gradId={`g${i}`} />
                        ))}
                    </ChartGrid>
                    <DataTable pts={[...pts].reverse()} columns={genericCols} sensorName={sensor.name} />
                </>);
            })()}
        </div>
    );
}
